import {
  ArrowTrendingUpIcon,
  CpuChipIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline'

import { Badge, Button, Card, CardContent, Spinner } from '../../components/ui'
import { CsvTableViewer } from '../../components/tabular'
import { useLagAccrualAgentReview, useLagAccrualRun } from './api'
import { formatCurrency, formatPercent } from './utils'

export default function DashboardPage() {
  const { data: run, isLoading, error } = useLagAccrualRun()
  const agentReview = useLagAccrualAgentReview()

  if (isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !run) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-red-600 dark:text-red-400">
          Unable to load lag accruals.
        </CardContent>
      </Card>
    )
  }

  const categoryRows = run.categories.map(category => ({
    Category: category.name,
    Accrual: formatCurrency(category.amount),
    '3-mo avg': formatCurrency(category.historical_average),
    Variance: formatPercent(category.variance_pct),
    Status: category.status === 'hitl_required' ? 'HITL' : 'Auto',
    Reason: category.hitl_reason ?? 'Within policy threshold',
    Debit: category.debit_account,
    Credit: category.credit_account,
    Department: category.department,
    Source: category.source_tab,
  }))
  const teRows = run.te_items.map(item => ({
    Employee: item.employee,
    Department: item.department,
    'Card balance': formatCurrency(item.card_balance),
    'Days outstanding': item.days_outstanding,
    'Estimated unsubmitted': formatCurrency(item.estimated_unsubmitted),
    Accrual: formatCurrency(item.accrual_amount),
    Receipt: item.receipt_attached,
  }))
  const legalRows = run.legal_items.map(item => ({
    Matter: item.matter_id,
    Firm: item.firm,
    Description: item.description,
    Hours: item.hours_estimate,
    Rate: item.rate,
    'Estimated unbilled': formatCurrency(item.estimated_unbilled),
    'Invoice lag': item.invoice_lag_days,
    Accrual: formatCurrency(item.accrual_amount),
  }))
  const eventRows = run.event_items.map(item => ({
    Date: item.date,
    Event: item.event,
    Vendor: item.vendor,
    Attendees: item.attendees,
    'Estimated cost': formatCurrency(item.estimated_cost),
    'Card posted': item.card_posted,
    'In GL': item.in_gl,
    Accrual: formatCurrency(item.accrual_amount),
    Flag: item.flag,
  }))
  const marketingRows = run.marketing_items.map(item => ({
    Program: item.program,
    Vendor: item.vendor,
    Committed: formatCurrency(item.committed_amount),
    'Deliverable due': item.deliverable_due,
    'Invoice lag': item.invoice_lag_estimate,
    Accrual: formatCurrency(item.accrual_amount),
  }))

  return (
    <div className="grid gap-6">
      <section className="grid gap-4 lg:grid-cols-[1.45fr_0.85fr_0.85fr]">
        <Card className="border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/10">
          <CardContent className="flex h-full items-start gap-4 p-5">
            <ExclamationTriangleIcon className="mt-1 h-6 w-6 shrink-0 text-red-600 dark:text-red-400" />
            <div>
              <h2 className="font-subtitle text-lg text-red-950 dark:text-red-100">
                Controller review required
              </h2>
              <p className="mt-2 text-sm leading-6 text-red-800 dark:text-red-200">
                Grand total {formatCurrency(run.grand_total)} exceeds the {formatCurrency(run.total_threshold)}{' '}
                policy threshold. All four accrual categories require HITL action before NetSuite upload.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Largest driver
                </p>
                <p className="mt-2 text-2xl font-semibold text-charcoal dark:text-gray-100">
                  {formatCurrency(Math.max(...run.categories.map(category => category.amount)))}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Legal IP matters
                </p>
              </div>
              <ScaleIcon className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Ready exports
                </p>
                <p className="mt-2 text-2xl font-semibold text-charcoal dark:text-gray-100">
                  2 files
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  JE and reversal balanced
                </p>
              </div>
              <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardContent className="grid gap-4 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <CpuChipIcon className="mt-0.5 h-5 w-5 text-eliza-red" />
              <div>
                <h2 className="font-subtitle text-lg text-charcoal dark:text-gray-100">
                  Controller review agent
                </h2>
                <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
                  HITL blockers, variance checks, and NetSuite readiness for the current close.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={agentReview.isPending}
              onClick={() => agentReview.mutate({ use_llm: false })}
            >
              {agentReview.isPending ? <Spinner size="sm" /> : <CpuChipIcon className="h-4 w-4" />}
              Run Review
            </Button>
          </div>

          {agentReview.data ? (
            <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="self-start rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{agentReview.data.mode}</Badge>
                  <Badge variant="secondary">
                    {agentReview.data.source_mode === 'redshift'
                      ? agentReview.data.redshift_schema
                      : 'fixture'}
                  </Badge>
                </div>
                <p className="mt-3 text-sm leading-6 text-gray-700 dark:text-gray-200">
                  {agentReview.data.summary}
                </p>
                {agentReview.data.langfuse_trace_id ? (
                  <p className="mt-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                    trace {agentReview.data.langfuse_trace_id}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-2">
                {agentReview.data.findings.slice(0, 5).map(finding => (
                  <div
                    key={`${finding.title}-${finding.category_id ?? 'run'}`}
                    className="rounded-md border border-gray-200 px-4 py-3 dark:border-dark-border"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        className={
                          finding.severity === 'blocker'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : finding.severity === 'warning'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                              : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                        }
                      >
                        {finding.severity}
                      </Badge>
                      <span className="font-medium text-charcoal dark:text-gray-100">
                        {finding.title}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                      {finding.detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {agentReview.error ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              Unable to run controller review agent.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <CsvTableViewer
        title="Category Summary"
        rows={categoryRows}
        columns={['Category', 'Accrual', '3-mo avg', 'Variance', 'Status', 'Reason', 'Debit', 'Credit', 'Department', 'Source']}
        filename="lag-accruals-category-summary.csv"
      />

      <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <CsvTableViewer
          title="T&E Lag Detail"
          rows={teRows}
          columns={['Employee', 'Department', 'Card balance', 'Days outstanding', 'Accrual', 'Receipt']}
          filename="lag-accruals-te-detail.csv"
          initialPageSize={10}
        />
        <CsvTableViewer
          title="Legal Services Detail"
          rows={legalRows}
          columns={['Matter', 'Firm', 'Description', 'Hours', 'Rate', 'Estimated unbilled', 'Invoice lag', 'Accrual']}
          filename="lag-accruals-legal-detail.csv"
          initialPageSize={10}
        />
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <CsvTableViewer
          title="Events Detail"
          rows={eventRows}
          columns={['Date', 'Event', 'Vendor', 'Attendees', 'Estimated cost', 'Card posted', 'In GL', 'Accrual', 'Flag']}
          filename="lag-accruals-events-detail.csv"
          initialPageSize={10}
        />
        <CsvTableViewer
          title="Marketing Detail"
          rows={marketingRows}
          columns={['Program', 'Vendor', 'Committed', 'Deliverable due', 'Invoice lag', 'Accrual']}
          filename="lag-accruals-marketing-detail.csv"
          initialPageSize={10}
        />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white px-5 py-4 dark:border-dark-border dark:bg-dark-surface">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ArrowTrendingUpIcon className="h-5 w-5 text-eliza-red" />
            <div>
              <p className="font-medium text-charcoal dark:text-gray-100">Next close action</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Complete HITL decisions, then upload the JE load and reversal files.
              </p>
            </div>
          </div>
          <Badge variant="secondary">Source: {run.source_files[0]?.name}</Badge>
        </div>
      </section>
    </div>
  )
}
